import { infiniteQueryOptions, queryOptions } from "@tanstack/react-query";

import { useApi } from "@/api/hooks/useApi";
import { commonAPIDataSelector } from "@/helpers/Network/selectors";
import { QUERY_KEYS } from "@/query-keys";
import type { ApiQueryParams } from "@/types/api-types";

import type { AdminTicketTabs } from "../tickets/constants";

export const usePortfolioTicketsQueries = () => {
  const api = useApi();

  return {
    userProfileTicketsConfiguration: () =>
      queryOptions({
        queryKey: QUERY_KEYS.TICKET_SETTINGS,
        queryFn: () => api.getUserProfileTicketsConfigurationV1(),
        select: commonAPIDataSelector,
      }),
    list: (query: ApiQueryParams<"getAdminPortfolioTicketsV1">) => {
      const limit = 30;

      return infiniteQueryOptions({
        queryKey: QUERY_KEYS.PORTFOLIO_TICKETS_LIST(query),
        queryFn: ({ pageParam = 0 }) =>
          api
            .getAdminPortfolioTicketsV1({
              Limit: limit,
              Offset: pageParam * limit,
              ...query,
            })
            .then((items) => commonAPIDataSelector(items)),
        initialPageParam: 0,
        getNextPageParam: (lastPage, pages) => (lastPage?.hasMore ? pages?.length : undefined),
      });
    },
    tabsStats: (query: ApiQueryParams<"getAdminPortfolioTicketsV1">) =>
      queryOptions({
        queryKey: QUERY_KEYS.PORTFOLIO_TICKETS_TABS_AMOUNT({
          ...query,
          Tab: undefined,
          SortBy: undefined,
          ResidentFocusMode: false,
        }),
        queryFn: () =>
          api.getAdminPortfolioTicketsStatsAllTabsV1({
            ...query,
            Tab: undefined,
            SortBy: undefined,
            ResidentFocusMode: false,
          }),
        select: commonAPIDataSelector,
      }),
    /** Returns the unfiltered tab total used as the denominator in the "filtered from" result label. */
    totalTicketsInTab: (tab: AdminTicketTabs) =>
      queryOptions({
        queryKey: QUERY_KEYS.PORTFOLIO_TICKETS_TAB_TOTAL(tab),
        queryFn: () => api.getAdminPortfolioTicketsStatsDetailsV1(tab),
        select: commonAPIDataSelector,
      }),
    ticketIds: (query: ApiQueryParams<"getAdminPortfolioTicketsV1">) =>
      queryOptions({
        queryKey: QUERY_KEYS.PORTFOLIO_TICKETS_IDS(query),
        queryFn: () => api.getAdminPortfolioTicketsIdsV1(query),
        select: commonAPIDataSelector,
      }),
    filterOptions: (query: ApiQueryParams<"getAdminPortfolioTicketsFiltersV1">) =>
      queryOptions({
        queryKey: QUERY_KEYS.PORTFOLIO_TICKETS_FILTERS(query),
        queryFn: () => api.getAdminPortfolioTicketsFiltersV1(query),
        select: commonAPIDataSelector,
        staleTime: 30 * 60 * 1000,
      }),
  };
};
